import type { WindowKey } from '@gonfalon/datetime/TimePager';
import { enableAiConfigMonitoringRangeBuckets } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { StaticMessageId } from '@gonfalon/messages/ids';

import type { DimensionFilterKey, MetricFilterKey, PerformanceMetricFilterKey } from './types';

export const MAX_INTERVALS = 32;

export const PERFORMANCE_METRIC_LABEL_IDS = {
  cost: 'aiMonitoring.metrics.cost',
  satisfaction: 'aiMonitoring.metrics.satisfaction',
  latency: 'aiMonitoring.metrics.latency',
  errorRate: 'aiMonitoring.metrics.errorRate',
  requests: 'aiMonitoring.metrics.requests',
} as const satisfies Record<PerformanceMetricFilterKey, StaticMessageId>;

export const METRIC_KEY_LABEL_MAP: Record<MetricFilterKey, string> = {
  tokens: 'Tokens',
  satisfaction: 'Satisfaction',
  generations: 'Generations',
  timeToGenerate: 'Time to generate',
  errorRate: 'Error rate',
  timeToFirstTokenMs: 'Time to first token',
  cost: 'Cost',
};

export const DIMENSION_LABEL_IDS = {
  none: 'aiMonitoring.groupBy.none',
  config: 'aiMonitoring.groupBy.config',
  model: 'aiMonitoring.groupBy.model',
  provider: 'aiMonitoring.groupBy.provider',
  agentGraph: 'aiMonitoring.groupBy.agentGraph',
  context: 'aiMonitoring.groupBy.context',
} as const satisfies Record<DimensionFilterKey, StaticMessageId>;

export const METRIC_KEYS = Object.keys(METRIC_KEY_LABEL_MAP) as readonly MetricFilterKey[];
export const PERFORMANCE_METRIC_KEYS = Object.keys(
  PERFORMANCE_METRIC_LABEL_IDS,
) as readonly PerformanceMetricFilterKey[];

export function isPerformanceMetricKey(key: string): key is PerformanceMetricFilterKey {
  return (PERFORMANCE_METRIC_KEYS as readonly string[]).includes(key);
}

export const DIMENSION_KEYS = Object.keys(DIMENSION_LABEL_IDS) as readonly DimensionFilterKey[];

export const ONE_DAY_MS = 24 * 60 * 60 * 1000;
export const ONE_HOUR_MS = 60 * 60 * 1000;

export const METRICS_WITH_ZERO_AS_UNDEFINED: readonly MetricFilterKey[] = [
  'tokens',
  'generations',
  'timeToGenerate',
  'timeToFirstTokenMs',
  'cost',
] as const;

export const TREND_EXPLORER_STORAGE_KEY = 'trend-explorer-preferences';
export const AI_INSIGHTS_STORAGE_KEY = 'ai-insights-preferences';
export const TREND_EXPLORER_STORAGE_METRIC_PARAM_KEY = 'metric';
export const TREND_EXPLORER_STORAGE_DIMENSIONS_PARAM_KEY = 'dimensions';
export const TREND_EXPLORER_STORAGE_CHART_TYPE_PARAM_KEY = 'chart-type';
/** Base64-encoded JSON array of `TrendExplorerMetricKey` (performance + judge metrics). */
export const TREND_EXPLORER_STORAGE_METRICS_SELECTION_PARAM_KEY = 'trend-metrics';

export const DURATION_PRESETS: WindowKey[] = ['P1D', 'P7D', 'P14D', 'P30D'];

export function getDurationPresets(): WindowKey[] {
  return enableAiConfigMonitoringRangeBuckets() ? ['PT1H', ...DURATION_PRESETS] : DURATION_PRESETS;
}

export type SelectField = {
  field: string;
  expression: string;
  rate?: string;
};

export const NON_JUDGE_SELECT_FIELDS: SelectField[] = [
  // Tokens (sum_avg_per_generation_count for display, sum for table aggregations)
  { field: 'tokens.input', expression: 'sum_avg_per_generation_count' },
  { field: 'tokens.output', expression: 'sum_avg_per_generation_count' },
  { field: 'tokens.input', expression: 'sum' },
  { field: 'tokens.output', expression: 'sum' },
  { field: 'tokens.total', expression: 'sum_avg_per_generation_count' },

  // Generations (needed as weight for weighted averages)
  { field: 'generation', expression: 'count' },
  { field: 'generation', expression: 'count', rate: 'day' }, // Per-day average for display
  { field: 'generation.success', expression: 'count' },
  { field: 'generation.error', expression: 'count' },
  { field: 'generation.error', expression: 'count_rate' }, // Error rate

  // Duration / Latency (sum_avg_per_generation_count for display, sum for table aggregations)
  { field: 'duration', expression: 'sum_avg_per_generation_count' },
  { field: 'duration', expression: 'sum' },

  // Time to First Token (sum_avg_per_generation_count for display, sum for table aggregations)
  { field: 'tokens.ttf', expression: 'sum_avg_per_generation_count' },
  { field: 'tokens.ttf', expression: 'sum' },

  // Feedback / Satisfaction
  { field: 'feedback.user.positive', expression: 'count' },
  { field: 'feedback.user.negative', expression: 'count' },
  { field: 'feedback.user.positive', expression: 'count_rate' }, // Satisfaction rate
  { field: 'feedback.user', expression: 'count' }, // Total feedback count (needed as weight for satisfaction)

  // Costs (sum for both chart display and table aggregations)
  { field: 'cost.tokens.input', expression: 'sum' },
  { field: 'cost.tokens.output', expression: 'sum' },
  { field: 'cost.tokens', expression: 'sum' }, // Total cost
];

const JUDGE_SELECT_FIELD_PREFIX = 'judge.';

const JUDGE_SELECT_FIELDS: SelectField[] = [
  // Judge Metrics (Evaluators)
  { field: `${JUDGE_SELECT_FIELD_PREFIX}accuracy`, expression: 'avg' },
  { field: `${JUDGE_SELECT_FIELD_PREFIX}accuracy`, expression: 'count' }, // Needed as weight
  { field: `${JUDGE_SELECT_FIELD_PREFIX}relevance`, expression: 'avg' },
  { field: `${JUDGE_SELECT_FIELD_PREFIX}relevance`, expression: 'count' }, // Needed as weight
  { field: `${JUDGE_SELECT_FIELD_PREFIX}toxicity`, expression: 'avg' },
  { field: `${JUDGE_SELECT_FIELD_PREFIX}toxicity`, expression: 'count' }, // Needed as weight
];

export const ALL_SELECT_FIELDS: SelectField[] = [...JUDGE_SELECT_FIELDS, ...NON_JUDGE_SELECT_FIELDS];
